import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as _ from "../../../constants/Constants";
import { checkUserRoles, checkMandatoryDocStatus } from "../../../utils/utils";
import {
  httpGetThreeNotesByProfileId,
  httpGetVendorProfileByEmail,
  httpGetVendorProfileById,
  httpGetVendorStatusDetails,
  httpGetMandatoryDocumentStatus,
} from "../../../service/httpClient";
import { updateServiceData } from "../../../redux/serviceSlice";
import { updateLeftNav } from "../../../redux/leftNavSlice";
import { updateCommonInfo } from "../../../redux/commonSlice";
import { verifyAccessPermission } from "../../../utils/rolePermissionAccess";

export const extractVendorStatus = (vendorDetailStatus, vendorStatus) => {
  let mutatedVendorStatus = {};
  if (vendorStatus.length) {
    vendorStatus.forEach((status, index) => {
      const statusValue = status?.vendorStatus?.lastStatusUpdatedValue || "";
      mutatedVendorStatus[status?.statusName] = statusValue;
      if (index === 0) {
        mutatedVendorStatus[_.ONBOARDING_STATUS] =
          status?.onboardingStatus || "";
        mutatedVendorStatus[_.MERCHANT_APPROVAL_ID] =
          vendorDetailStatus || _.PENDING;
      }
    });
  }
  return { mutatedVendorStatus };
};

const filterVendorProfileId = (vendorData = {}) => {
  let vendorProfileId = "";
  for (const dataKey in vendorData) {
    if (
      vendorData[dataKey] &&
      typeof vendorData[dataKey] === "object" &&
      !Array.isArray(vendorData[dataKey]) &&
      vendorData[dataKey].vendorProfileId
    ) {
      vendorProfileId = vendorData[dataKey].vendorProfileId;
      break;
    }
  }
  return vendorProfileId;
};

export const useVendorProfileLoader = () => {
  const [vendorSelected, setVendorSelected] = useState(true);
  const [documentStatusRes, setDocumentStatusRes] = useState({});
  const dispatch = useDispatch();
  const store = useSelector(
    ({ service, common }) => ({
      fetching: service.fetching,
      vendorProfileId: service.vendorProfile?.vendorDetail?.id || "",
      profileId: common.profileId,
      externalVendorEmail: common?.externalVendorEmail,
      onboardingStatus: common.boardingStatuses?.onboardingStatus,
      roleName: service.roles?.roleName,
      vendorStatus: service.vendorStatus || [],

      extUserEnabled: verifyAccessPermission("extUserEnabled")
        ? service?.vendorProfile && service?.vendorProfile?.primaryContact
          ? true
          : false
        : true,
      merchandiseType:
        service.vendorProfile?.vendorDetail?.merchandiseType || "",
    }),
    shallowEqual
  );

  useEffect(() => {
    const { vendorProfileId, profileId, fetching, externalVendorEmail } = store;
    const fetchVendor = vendorProfileId.toString() !== profileId || fetching;

    setVendorSelected(profileId || externalVendorEmail);

    if (profileId && fetchVendor) {
      localStorage.setItem("vid", profileId);
      fetchVendorData(profileId);
    }

    if (!profileId && externalVendorEmail && fetching) {
      httpGetVendorProfileByEmail(externalVendorEmail)
        .then((res) => {
          const vendorProfileId = filterVendorProfileId(res.data || {});
          if (vendorProfileId) {
            fetchVendorData(vendorProfileId, false);
          } else {
            extractVendorProfile(res.data);
          }
        })
        .catch((err) => console.log(err));
    }
  }, [store.profileId, store.externalVendorEmail]);

  const getUpdatedVendorData = (vendorProfileId) => {
    return new Promise((resolve, reject) => {
      Promise.allSettled([
        httpGetVendorProfileById(vendorProfileId),
        httpGetVendorStatusDetails(vendorProfileId),
        httpGetMandatoryDocumentStatus(vendorProfileId),
      ])
        .then(([profileRes, statusRes, documentStatusRes]) => {
          let vendorProfile = profileRes.value?.data;
          let vendorStatus = statusRes.value?.data;
          let mandatoryDocStatusRes = checkMandatoryDocStatus(
            store.onboardingStatus,
            documentStatusRes.value?.data
          );
          const mandatoryDocRes = documentStatusRes.value?.data || {};

          setDocumentStatusRes(documentStatusRes.value?.data || {});
          extractVendorProfile(
            vendorProfile,
            vendorStatus,
            mandatoryDocStatusRes,
            mandatoryDocRes
          );
          resolve();
        })
        .catch((err) => {
          setDocumentStatusRes({});
          reject(err);
        });
    });
  };

  const fetchVendorData = (vendorProfileId, getNotes = true) => {
    getUpdatedVendorData(vendorProfileId).catch((err) => console.log(err));

    getNotes && getTopThreeNotes(vendorProfileId);
  };

  const getTopThreeNotes = (profileId = null) => {
    const vendorProfileId = store.profileId || profileId || "";
    if (vendorProfileId) {
      httpGetThreeNotesByProfileId(vendorProfileId)
        .then((res) => {
          dispatch(
            updateServiceData({
              notes: res.data,
            })
          );
        })
        .catch((error) => console.log(error));
    }
  };

  const extractVendorProfile = (
    vendorProfile,
    vendorStatus = [],
    mandatoryDocStatusRes = false,
    mandatoryDocRes = {}
  ) => {
    const {
      vendorDetail,
      primaryContact,
      mailAddress,
      merchantContact,
      multipleMerchantContactList,
    } = vendorProfile || {};
    const { duns, vendorConnectionPlatform, vendorCorpName, status } =
      vendorDetail || {};
    const { mutatedVendorStatus } = extractVendorStatus(
      status || "",
      vendorStatus
    );

    dispatch(
      updateServiceData({
        vendorProfile,
        vendorStatus,
        fetching: false,
        merchantContact,
        multipleMerchantContactList,
      })
    );
    dispatch(
      updateLeftNav({
        primaryContact: {
          userEmail: primaryContact?.userEmail || "",
          vendorPhone: primaryContact?.phoneNumber || "",
          streetAddress: mailAddress?.streetAddress || "",
          city: mailAddress?.city || "",
          state: mailAddress?.state || "",
          zipCode: mailAddress?.zipCode || "",
        },
      })
    );
    dispatch(
      updateCommonInfo({
        boardingStatuses: mutatedVendorStatus,
        vendorCorpName: vendorCorpName || "",
        duns: duns || "",
        userEmail: primaryContact?.userEmail || "",
        vendorConnectionPlatform: vendorConnectionPlatform || "",
        vendorPhone: primaryContact?.phoneNumber || "",
        profileId: vendorDetail?.id?.toString() || store.profileId || "",
        mandatoryDocStatusRes: mandatoryDocStatusRes || false,
        mandatoryDocRes: mandatoryDocRes || {},
      })
    );
  };
};
